import { useCallback, useMemo } from 'react';
import { useLanguage } from '../i18n';
import {
  formatDate,
  formatNumber,
  formatCurrency,
  formatRelativeTime,
  formatList,
  formatDateRange,
  formatHeight,
  formatWeight,
  type DateFormatOptions,
  type CurrencyFormatOptions,
} from '../i18n/formatters';
import type { SupportedLanguage } from '../../i18n';

/**
 * Hook that provides localized formatting functions based on current language
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { formatDate, formatCurrency, formatRelativeTime } = useLocalizedFormatters();
 *
 *   return (
 *     <div>
 *       <p>Date: {formatDate(new Date())}</p>
 *       <p>Price: {formatCurrency(99.99)}</p>
 *       <p>Posted: {formatRelativeTime(createdAt)}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export function useLocalizedFormatters() {
  const { currentLanguage } = useLanguage();

  const localizedFormatDate = useCallback(
    (date: Date | string | number, options?: DateFormatOptions) =>
      formatDate(date, currentLanguage, options),
    [currentLanguage]
  );

  const localizedFormatNumber = useCallback(
    (num: number, options?: Intl.NumberFormatOptions) =>
      formatNumber(num, currentLanguage, options),
    [currentLanguage]
  );

  const localizedFormatCurrency = useCallback(
    (amount: number, options?: CurrencyFormatOptions) =>
      formatCurrency(amount, currentLanguage, options),
    [currentLanguage]
  );

  const localizedFormatRelativeTime = useCallback(
    (date: Date | string | number) => formatRelativeTime(date, currentLanguage),
    [currentLanguage]
  );

  const localizedFormatList = useCallback(
    (items: string[], type?: 'conjunction' | 'disjunction') =>
      formatList(items, currentLanguage, type),
    [currentLanguage]
  );

  const localizedFormatDateRange = useCallback(
    (startDate: Date | string | number, endDate: Date | string | number) =>
      formatDateRange(startDate, endDate, currentLanguage),
    [currentLanguage]
  );

  const localizedFormatHeight = useCallback(
    (heightCm: number) => formatHeight(heightCm, currentLanguage),
    [currentLanguage]
  );

  const localizedFormatWeight = useCallback(
    (weightKg: number) => formatWeight(weightKg, currentLanguage),
    [currentLanguage]
  );

  return useMemo(
    () => ({
      formatDate: localizedFormatDate,
      formatNumber: localizedFormatNumber,
      formatCurrency: localizedFormatCurrency,
      formatRelativeTime: localizedFormatRelativeTime,
      formatList: localizedFormatList,
      formatDateRange: localizedFormatDateRange,
      formatHeight: localizedFormatHeight,
      formatWeight: localizedFormatWeight,
      currentLanguage: currentLanguage as SupportedLanguage,
    }),
    [
      localizedFormatDate,
      localizedFormatNumber,
      localizedFormatCurrency,
      localizedFormatRelativeTime,
      localizedFormatList,
      localizedFormatDateRange,
      localizedFormatHeight,
      localizedFormatWeight,
      currentLanguage,
    ]
  );
}

export default useLocalizedFormatters;
